/**
 * Screen-capture detection policy for the web client.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ScreenshotDetectionPolicy.swift` —
 * same three-level enum (ignore / logAudit / logAuditAndNotifyPeer), same
 * per-conversation opt-in model.
 *
 * PLATFORM GAPS (both documented here and in docs/screen-security.md):
 *
 *   1. HARD GAP — Screenshot detection: `UIApplicationUserDidTakeScreenshotNotification`
 *      has NO web equivalent. Browsers provide zero signal when a user takes a
 *      screenshot. This policy only handles screen-*sharing* detection (via
 *      getDisplayMedia), not screenshot detection.
 *
 *   2. PARTIAL — Screen-sharing detection: iOS uses `UIScreen.capturedDidChangeNotification`
 *      which fires for ANY capture (AirPlay mirror, screen recording, QuickTime).
 *      On the web, detection is limited to browser-initiated `getDisplayMedia` sessions
 *      that the user started. Passive OS-level capture (e.g. screen recording) is
 *      undetectable.
 *
 * Pure functions only. NO getDisplayMedia calls. NO DOM listeners.
 */

import type { ScreenSecuritySettings } from "./ScreenSecuritySettings";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Per-conversation screen-capture policy setting.
 *
 * Mirrors iOS `ScreenshotDetectionPolicy` enum:
 *   - `ignore`:                 take no action (default)
 *   - `logAudit`:               record an audit event locally, no peer notification
 *   - `logAuditAndNotifyPeer`:  record + attempt to notify the peer (requires
 *                               BOTH global `notifyPeerOnCapture` AND peer opt-in)
 */
export type ScreenCaptureConversationSetting =
  | "ignore"
  | "logAudit"
  | "logAuditAndNotifyPeer";

/**
 * A screen-capture event presented to the policy function.
 *
 * Web analogue: fired when `getDisplayMedia` stream is acquired or when
 * a previously-acquired capture stream ends.
 *
 * iOS analogue: `UIScreen.capturedDidChangeNotification` payload.
 */
export interface ScreenCaptureEvent {
  /** Unix timestamp (ms) when the capture was detected. */
  readonly detectedAt: number;
  /** Conversation this event is associated with (channelId or `1on1:<userId>`). */
  readonly conversationId: string;
  /**
   * Whether the peer has opted in to receiving capture notifications for this
   * conversation. Only relevant when the per-conversation setting is
   * `logAuditAndNotifyPeer` AND global `notifyPeerOnCapture` is true.
   */
  readonly peerHasOptedIn: boolean;
}

/** The decision returned by `decide`. */
export type ScreenCapturePolicyDecision =
  | "ignore"
  | "logAudit"
  | "logAuditAndNotifyPeer";

// ---------------------------------------------------------------------------
// Policy function
// ---------------------------------------------------------------------------

/**
 * Decide what action to take when a screen-capture event is detected.
 *
 * Two-level gate:
 *   1. Global `settings.screenCaptureDetection` must be true (else → ignore).
 *   2. Per-conversation `perConversationSetting` controls the action level.
 *   3. `logAuditAndNotifyPeer` additionally requires:
 *      - Global `settings.notifyPeerOnCapture` is true (user has enabled the feature globally)
 *      - `event.peerHasOptedIn` is true (peer has consented to receive notifications)
 *      If either gate fails, the decision downgrades to `logAudit`.
 *
 * iOS analogue: `ScreenshotDetectionPolicy.decide(event:, conversationSetting:, globalSettings:)`.
 *
 * @param event                  - The capture event.
 * @param perConversationSetting - The per-conversation opt-in level.
 * @param settings               - Global screen-security settings.
 */
export function decide(
  event: ScreenCaptureEvent,
  perConversationSetting: ScreenCaptureConversationSetting,
  settings: Pick<
    ScreenSecuritySettings,
    "screenCaptureDetection" | "notifyPeerOnCapture"
  >,
): ScreenCapturePolicyDecision {
  // Gate 1: global detection must be enabled.
  if (!settings.screenCaptureDetection) {
    return "ignore";
  }

  // Gate 2: per-conversation setting.
  if (perConversationSetting === "ignore") {
    return "ignore";
  }

  if (perConversationSetting === "logAudit") {
    return "logAudit";
  }

  // perConversationSetting === "logAuditAndNotifyPeer"
  // Gate 3: global notify-peer flag AND peer opt-in.
  if (settings.notifyPeerOnCapture && event.peerHasOptedIn) {
    return "logAuditAndNotifyPeer";
  }

  // Downgrade: we can still audit locally even if peer notification is gated.
  return "logAudit";
}
